import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { permissionsApi, sessionPermissionTag } from "permissions/_api/permissions-api-service";
import { populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { Navigate, useLocation } from "react-router";
import Session from "users/__session/session";
import { AccountType } from "users/account-types/account-type";
import { useGetUserInformationQuery } from "users/information/_api/user-information-api";
import UserInformation from "users/information/user-information";

export const SessionContext = createContext<Session>(undefined!);

/**
 * `setTimeout` stores its delay in a signed 32-bit integer. A longer delay overflows and fires
 * immediately, so an expiry further out than this is waited for in instalments.
 */
const LONGEST_TIMEOUT_MS = 2_147_483_647;

export function shouldPollForOnboardingCompletion(session: Session | undefined): boolean {
  return Boolean(session?.isLoggedIn && session.onboardingWork);
}

export function shouldRefreshSessionPermissionsAfterOnboarding(
  wasRestricted: boolean,
  onboardingState: NonNullable<UserInformation["onboardingWork"]>["state"] | undefined,
): boolean {
  return wasRestricted && (onboardingState === undefined || onboardingState === "Completed");
}

export function SessionProvider({ children }: { readonly children: React.ReactNode }) {
  const [updatedSession, setSession] = useState<Session>();
  const dispatch = useAttorneyHubDispatch();

  /**
   * Restored on the first render rather than inside the mount effect below, because the value
   * this provider publishes has to be a real session from the very first render.
   *
   * It used to be the `useState` alone, filled in only once that effect's `await` resolved --
   * a network call wide. `value={session!}` was nullish for the whole of that window, and
   * `useSession` answers a nullish context by loading a SECOND session from storage whose
   * updater does nothing but warn. Anything that rendered in that window therefore held a
   * detached session: every read worked, which is why this went unseen, and every write through
   * it was dropped on the floor.
   *
   * `AccountSwitcher` is where it surfaced. Open the account menu while the app is still
   * booting and "View as vendor" persists to storage, calls the API, builds the new session --
   * and hands it to a callback that does nothing, so the view never changes. It cost
   * `marketplace/team-fee-schedules.spec.ts` one to three tests a run for weeks.
   */
  const restoredSession = useMemo(() => Session.loadFromStorage(setSession), []);
  const session = updatedSession ?? restoredSession;

  const userInformationQuery = useGetUserInformationQuery(
    { session },
    {
      skip: !session?.isLoggedIn,
      pollingInterval: shouldPollForOnboardingCompletion(session) ? 3000 : 0,
    },
  );
  const sessionUserId = session?.user?.id?.value;
  const sessionAuthToken = session?.authToken?.value;
  const sessionExpiresAt = session?.authToken?.tokenExp?.value;

  /**
   * Tells the server which view this session was left in. The session itself is already restored
   * by the time this runs -- all this adds is the round trip, and `setAccountViewType`
   * republishes through `setSession` on its own when it changes anything.
   */
  useEffect(() => {
    const accountType: AccountType = restoredSession.context?.viewingAsVendor
      ? AccountType.Vendor
      : AccountType.Client;
    void restoredSession.setAccountViewType(accountType);
  }, [restoredSession]);

  /**
   * Signs the session out at the moment its token expires.
   *
   * Expiry is a clock event, and nothing in React observes the clock: until this, a token that
   * lapsed while the tab sat open left the app rendering an authenticated shell over a dead
   * credential, and only the next navigation that happened to re-run a route guard noticed. Logging
   * out here clears the stale credential, drops the realtime connection, and lands the person on
   * the login form rather than on a page where every request now fails.
   */
  useEffect(() => {
    if (!session?.isLoggedIn || sessionExpiresAt === undefined) return;

    let expiryTimer: ReturnType<typeof setTimeout>;
    const signOutOnExpiry = () => {
      const millisecondsUntilExpiry = Math.max(sessionExpiresAt - Date.now(), 0);
      expiryTimer = setTimeout(() => {
        if (session.authToken?.isExpired) void session.logout();
        else signOutOnExpiry();
      }, Math.min(millisecondsUntilExpiry, LONGEST_TIMEOUT_MS));
    };
    signOutOnExpiry();

    return () => clearTimeout(expiryTimer);
  }, [session, sessionExpiresAt]);

  useEffect(() => {
    if (!session?.isLoggedIn || !sessionUserId || !sessionAuthToken) return;

    const request = dispatch(populateSessionPermissions({ session }));
    return () => request.unsubscribe?.();
  }, [dispatch, session, sessionAuthToken, sessionUserId]);

  useEffect(() => {
    if (session?.isLoggedIn && userInformationQuery.currentData) {
      const userInfo = UserInformation.fromJSON(userInformationQuery.currentData);
      if (!userInfo) {
        throw new Error("Failed to parse user information from API");
      }
      const refreshSessionPermissions = shouldRefreshSessionPermissionsAfterOnboarding(
        session.isRestrictedOnboarding,
        userInfo.onboardingWork?.state,
      );
      session.refreshFromUserInfo(userInfo);
      if (refreshSessionPermissions) {
        dispatch(permissionsApi.util.invalidateTags([sessionPermissionTag]));
      }
    }
  }, [dispatch, session, userInformationQuery.currentData]);

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  let context = useContext?.(SessionContext);

  //#2188 bug fix for useSession being called before the context is initialized in the SessionProvider
  //removing the session instantiation will result in a provider error.
  if (!context) {
    context = Session.loadFromStorage(() => {
      console.warn("Session not initialized yet");
    });
  }

  return context;
}

export function RequireSession({ children }: { readonly children: React.JSX.Element }) {
  const session = useSession();
  const location = useLocation();

  if (!session.isLoggedIn) {
    // Redirect user to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to forward them
    // along to that page after they log in.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}
